/**
 * SyncStorage — platform-agnostic storage interface for the sync engine.
 *
 * ============================================================================
 * WHY THIS EXISTS
 * ============================================================================
 *
 * The sync engine (Zustand store + DO sync hook) needs to persist data locally.
 * On web, that's Dexie/IndexedDB. On React Native, it will be SQLite (or
 * another local DB). This interface decouples the sync engine from the specific
 * storage implementation so the same sync logic works on both platforms.
 *
 * ============================================================================
 * ARCHITECTURE
 * ============================================================================
 *
 *   ┌─────────────────────────────────────────────────────────┐
 *   │                    Sync Engine                          │
 *   │  ┌──────────────────┐    ┌─────────────────────────┐   │
 *   │  │  flashka-store   │    │  use-user-do-sync hook  │   │
 *   │  │  (Zustand)       │    │  (WebSocket sync)       │   │
 *   │  └────────┬─────────┘    └────────┬────────────────┘   │
 *   │           │                       │                    │
 *   │           ▼                       ▼                    │
 *   │  ┌────────────────────────────────────────────────┐    │
 *   │  │            SyncStorage interface               │    │
 *   │  └────────────────────┬───────────────────────────┘    │
 *   └──────────────────────-┼────────────────────────────────┘
 *                           │
 *            ┌──────────────┼──────────────┐
 *            ▼                             ▼
 *   ┌────────────────┐           ┌────────────────┐
 *   │ DexieSyncStorage│           │ SQLiteSyncStorage│
 *   │ (Web/IndexedDB) │           │ (React Native)  │
 *   └────────────────┘           └────────────────┘
 *
 * ============================================================================
 * HOW TO USE (for AI agents implementing new platforms)
 * ============================================================================
 *
 * 1. Implement the `SyncStorage` interface for your platform (see
 *    `DexieSyncStorage` in `./dexie-sync-storage.ts` as the reference).
 *
 * 2. Register your implementation BEFORE the Zustand store hydrates.
 *    On web, this is done per-user via `initDexieSyncStorage(userId)`:
 *
 *      import { initDexieSyncStorage } from "../dexie-sync-storage";
 *      initDexieSyncStorage(userId); // opens Flashka-{userId} DB
 *
 *    On React Native, you'd call `setSyncStorage()` directly:
 *
 *      import { setSyncStorage } from "../provider/sync-storage";
 *      import { MySQLiteStorage } from "./my-sqlite-storage";
 *      setSyncStorage(new MySQLiteStorage(userId));
 *
 * 3. That's it. The store and sync hook use `getSyncStorage()` internally.
 *
 * ============================================================================
 * IMPORTANT NOTES FOR IMPLEMENTORS
 * ============================================================================
 *
 * - All methods are async. Even if your storage is synchronous, return Promises.
 * - `putDeck` / `putDeckProperties` are upserts (insert or replace by key).
 * - `replaceAll` must atomically clear a table and insert the new set.
 *   Use a transaction if your platform supports it.
 * - `getSetting` / `putSetting` store arbitrary JSON-serializable values by key.
 *   The sync engine uses keys: "doSyncTimestamps", "globalSrSettings".
 * - `clearAll` wipes ALL tables. With per-user databases this is rarely
 *   needed (no clearAll on sign-out). Kept for debugging/data-reset scenarios.
 * - Fire-and-forget writes: the Zustand store calls writes without awaiting.
 *   Your implementation should handle this gracefully (no data loss on rapid writes).
 * - The notification system (`registerStoreNotify` / `notifyStore`) is NOT part of
 *   this interface — it lives in the store layer and is platform-independent.
 *
 * ============================================================================
 * REACT NATIVE MIGRATION GUIDE
 * ============================================================================
 *
 * When porting to React Native, follow these steps:
 *
 * STEP 1 — Create a SQLite implementation
 * ────────────────────────────────────────
 * Create `sqlite-sync-storage.ts` implementing `SyncStorage`. Suggested libs:
 *   - expo-sqlite (Expo managed workflow)
 *   - op-sqlite (bare workflow, fastest)
 *   - @op-engineering/op-sqlite
 *
 * Suggested SQLite schema:
 *
 *   CREATE TABLE decks (
 *     name TEXT PRIMARY KEY,
 *     cards TEXT NOT NULL,          -- JSON array of CardProps
 *     updatedAtISO TEXT
 *   );
 *   CREATE TABLE deck_properties (
 *     name TEXT PRIMARY KEY,
 *     data TEXT NOT NULL            -- JSON object of DeckProperties
 *   );
 *   CREATE TABLE folders (
 *     id TEXT PRIMARY KEY,
 *     data TEXT NOT NULL            -- JSON object of Folder
 *   );
 *   CREATE TABLE deck_notes (
 *     name TEXT PRIMARY KEY,
 *     content TEXT NOT NULL         -- JSON object of Record<string, NoteEntry>
 *   );
 *   CREATE TABLE settings (
 *     key TEXT PRIMARY KEY,
 *     value TEXT NOT NULL           -- JSON-serialized value
 *   );
 *   CREATE TABLE deck_source (name TEXT PRIMARY KEY, source TEXT);
 *   CREATE TABLE deck_pdf (name TEXT PRIMARY KEY, pdf BLOB);
 *
 * Each SyncStorage method maps to simple SQL:
 *   getAllDecks()         → SELECT * FROM decks
 *   putDeck(deck)        → INSERT OR REPLACE INTO decks VALUES (?, ?, ?)
 *   deleteDeck(name)     → DELETE FROM decks WHERE name = ?
 *   getAll(table)        → SELECT * FROM {table}
 *   put(table, item)     → INSERT OR REPLACE INTO {table} VALUES (...)
 *   replaceAll(table, items) → BEGIN; DELETE FROM {table}; INSERT ...; COMMIT
 *   getSetting(key)      → SELECT value FROM settings WHERE key = ?
 *   putSetting(key, val) → INSERT OR REPLACE INTO settings VALUES (?, ?)
 *   ... etc.
 *
 * STEP 2 — Register the implementation (per-user databases)
 * ──────────────────────────────────────────────────────────
 * Each user gets their own database (e.g. `flashka-{userId}.db`). This
 * eliminates the need for clearAll() on account switch.
 *
 * In your RN auth provider, when the userId is available:
 *
 *   import { setSyncStorage } from "../provider/sync-storage";
 *   import { SQLiteSyncStorage } from "./sqlite-sync-storage";
 *
 *   const db = openDatabase(`flashka-${userId}.db`);
 *   setSyncStorage(new SQLiteSyncStorage(db));
 *
 * STEP 3 — Swap the notification bridge in flashka-store.ts
 * ──────────────────────────────────────────────────────────
 * The bottom of `src/store/flashka-store.ts` has a web-specific section:
 *
 *   import { registerStoreNotify } from "@/database/db";
 *
 * On RN, replace this import:
 *   - Remove `import { registerStoreNotify } from "@/database/db"`
 *   - Instead, set up an equivalent notification system in your SQLite layer.
 *
 * The notification bridge exists because legacy web code calls
 * `localDB.updateDeckCards()` directly (e.g. deck-actions-utils.tsx,
 * study-session.tsx). When those writes happen, the Zustand store needs to
 * update its in-memory state. On RN you have two options:
 *
 *   Option A (recommended): Refactor those call sites to go through the
 *   Zustand store's `setDeck()` method instead of calling the DB directly.
 *   Then no notification bridge is needed.
 *
 *   Option B: Implement a similar callback system in your SQLite storage
 *   class, emitting events after writes that the store subscribes to.
 *
 * STEP 4 — PartySocket on React Native
 * ─────────────────────────────────────
 * PartySocket supports React Native out of the box. The import is the same:
 *
 *   import PartySocket from "partysocket";
 *
 * However, ensure your RN environment provides a global `WebSocket` (React
 * Native does by default). The `use-user-do-sync.ts` hook uses React hooks
 * (useEffect, useState, etc.) which work identically in RN.
 *
 * WHAT STAYS UNCHANGED (no modifications needed)
 * ───────────────────────────────────────────────
 * - `src/store/flashka-store.ts` — all store logic (except the bridge swap)
 * - `src/hooks/use-user-do-sync.ts` — the full sync hook
 * - `src/contexts/sync-context.tsx` — the SyncProvider
 * - `party/` — the entire server (Durable Objects)
 * - `party/sync-types.ts` — the sync protocol types
 *
 * GOTCHAS
 * ───────
 * - Fire-and-forget writes: The Zustand store calls storage methods without
 *   `await`. Your SQLite implementation must handle rapid sequential writes
 *   without data loss. Use WAL mode (`PRAGMA journal_mode=WAL`) for this.
 *
 * - JSON serialization: Settings values and card arrays are stored as JSON
 *   strings in SQLite. Parse on read, stringify on write. Be careful with
 *   `undefined` vs `null` — JSON.stringify drops `undefined` properties.
 *
 * - Transactions: `replaceAll()` must be atomic (clear + bulk insert).
 *   Wrap in a SQLite transaction. Same for `clearAll()`.
 *
 * - The `@tiptap/react` import for `JSONContent` type: On RN you may need
 *   to alias this to `@tiptap/core` or define the type inline, since
 *   TipTap's React package may not be available.
 *
 * - `navigator.onLine` / online/offline events: These don't exist in RN.
 *   Use `@react-native-community/netinfo` instead. The `useIsOnline()` hook
 *   in `use-user-do-sync.ts` would need a RN-specific implementation.
 *
 * ============================================================================
 * TYPES
 * ============================================================================
 *
 * The types below (Deck, DeckProperties, Folder, etc.) are also defined in
 * `db.ts` for backward compatibility. Both export the same shapes. When
 * implementing a new platform, import from this file.
 */

import type {
  DeckId,
  JSONContent,
  SyncCardProps as CardProps,
  SyncDeckMeta,
  SyncFolder,
  SyncNoteEntry,
  SyncQuizProgressEntry,
} from '@flashka/shared/sync-engine/schema/shared-types';

import type { DeckStudySummary } from '../deck-summary/summary';

// ---------------------------------------------------------------------------
// Domain types — re-exported from shared-types.ts (single source of truth)
// ---------------------------------------------------------------------------

/** Deck properties. Alias for `SyncDeckMeta` from shared types. */
export type DeckProperties = SyncDeckMeta;

/** Folder. Alias for `SyncFolder` from shared types. */
export type Folder = SyncFolder;

/** Note entry. Alias for `SyncNoteEntry` from shared types. */
export type NoteEntry = SyncNoteEntry;

/** Quiz progress entry. Alias for `SyncQuizProgressEntry` from shared types. */
export type QuizProgressEntry = SyncQuizProgressEntry;

export type { JSONContent };

// ---------------------------------------------------------------------------
// Frontend-specific composite types
// ---------------------------------------------------------------------------

export interface Deck {
  name: string;
  cards: CardProps[];
  updatedAtISO?: string;
}

/** A card row in the normalized `cards` table (card + its deck ID). */
export type StoredCard = CardProps & { deckId: DeckId };

/**
 * A row of the client-local `deckStudySummary` table
 * (`sync-engine/framework/local-tables.ts`): one deck's `DeckStudySummary`
 * keyed by its sync id. Derived from the deck's rows on this device and never
 * synced — see `sync-client/deck-summary`.
 */
export type StoredDeckSummary = DeckStudySummary & {
  deckId: DeckId;
  /** `DECK_STUDY_SUMMARY_VERSION` at write time. A row from an older version
   *  is recomputed at boot rather than trusted (a newer field would be
   *  missing from it). Optional only because the first rows carried none. */
  version?: number;
  /**
   * Set when card frames reached a deck whose full row set was NOT in memory,
   * so the counts may lag the rows on disk until the next full recompute
   * clears it. Persisted so a kill between the two does not hide the lag.
   */
  staleAtISO?: string | null;
};

/**
 * A study group's card bodies for ONE deck — a row of the client-local
 * `sgDeckCards` table (`SG_DECK_CARDS_TABLE`), keyed by (groupId, deckId).
 * Written by the study-group store's persist diff for the decks whose bucket
 * changed; read per group at hydrate. The group's snapshot row carries
 * everything else.
 */
export type StoredSGDeckCards = {
  groupId: string;
  deckId: string;
  cards: Record<string, CardProps>;
  savedAt: string;
};

export interface DeckNotes {
  name: string;
  content: Record<string, NoteEntry>;
}

// ---------------------------------------------------------------------------
// SyncStorage interface
// ---------------------------------------------------------------------------

/**
 * Platform-agnostic local storage contract used by the sync engine.
 *
 * Web implementation: `DexieSyncStorage` (wraps Dexie/IndexedDB)
 * React Native: `ExpoSqliteRingBufferStorage` (ring-buffer + ops_log)
 *
 * Write methods are fire-and-forget by design — the underlying adapter
 * coalesces multiple writes per JS tick into one transaction. Callers
 * that need durable completion (tests, app-background) should await
 * `flush()`. Reads transparently flush before reading so they observe
 * any in-flight writes from the current tick.
 */
export interface SyncStorage {
  /**
   * Capability: `cards` rows are stored per `(deckId, id)`, so two decks that
   * hold the same card id (a fork, a copy to personal) own two separate rows
   * and a write to one deck never touches the other deck's row.
   *
   * The deck slice relies on it to persist only the cards whose reference
   * changed (`updateDeckCards` / `setDeckFromSync`): that is only correct when
   * an unchanged card's row is still THIS deck's row. On a store keyed by `id`
   * alone, another deck's write of the same id takes the row over, and only a
   * whole-deck rewrite of this deck gives it back — so without the flag the
   * slice rewrites every row of the deck (still as one batch).
   *
   * Absent means "not known to be per-deck" and selects the SAFE behaviour.
   * Set it only when the adapter's cards store really is keyed per deck.
   */
  readonly cardRowsKeyedByDeck?: boolean;
  /** Fetch all rows from a table. */
  getAll<T>(table: string): Promise<T[]>;
  /** Fetch a single row by primary key. */
  getByKey<T>(table: string, key: string | unknown[]): Promise<T | undefined>;
  /** Upsert an item into a table. Fire-and-forget. */
  put<T>(table: string, item: T): void;
  /** Partial update of an item by primary key. Fire-and-forget. */
  updateByKey<T>(table: string, key: string, partial: Partial<T>): void;
  /** Delete an item by primary key. Fire-and-forget. */
  removeByKey(table: string, key: string | unknown[]): void;
  /** Atomically replace all rows in a table. Fire-and-forget. */
  replaceAll<T>(table: string, items: T[]): void;
  /**
   * Upsert many items into a table in as few transactions as possible, and
   * RESOLVE when the batch is durably persisted (unlike fire-and-forget
   * `put`). The sync engine awaits this to apply backpressure while applying
   * a large initial `sync-diff`, so writes drain incrementally instead of
   * piling up tens of thousands of in-flight ops and OOM-ing the renderer.
   *
   * Optional: callers MUST fall back to `put` + `flush` when an adapter does
   * not implement it (native's ring buffer already coalesces per-tick puts,
   * so the fallback is bounded there).
   */
  bulkPut?<T>(table: string, items: T[]): Promise<void>;
  /**
   * Delete many rows by primary key in as few transactions as possible.
   * Fire-and-forget, same key forms as `removeByKey`.
   *
   * Optional: callers fall back to one `removeByKey` per key when an adapter
   * does not implement it (see `safeBulkRemove`).
   */
  bulkRemove?(table: string, keys: Array<string | unknown[]>): void;
  /** Fetch all rows matching a secondary index value. */
  getByIndex<T>(table: string, indexName: string, value: string): Promise<T[]>;
  /** Delete all rows matching a secondary index value. Fire-and-forget. */
  removeByIndex(table: string, indexName: string, value: string): void;
  /** Wipe all data. Called on user sign-out. */
  clearAll(): Promise<void>;
  /**
   * Drain any in-flight buffered writes. Resolves when all queued ops
   * have been persisted to disk. Tests and app-background should await.
   */
  flush(): Promise<void>;
}

// ---------------------------------------------------------------------------
// Global singleton — set once at app startup, used everywhere
// ---------------------------------------------------------------------------

let _storage: SyncStorage | null = null;

/**
 * Register the platform-specific storage implementation.
 * Must be called before the Zustand store hydrates (typically in your
 * app's entry point or provider).
 *
 * @example
 *   // Web (already done in DexieSyncStorage module)
 *   import "../dexie-sync-storage"; // auto-registers
 *
 *   // React Native (you'd create this)
 *   import { setSyncStorage } from "../provider/sync-storage";
 *   import { SQLiteSyncStorage } from "./sqlite-sync-storage";
 *   setSyncStorage(new SQLiteSyncStorage(db));
 */
export function setSyncStorage(storage: SyncStorage): void {
  _storage = storage;
}

/**
 * Get the registered storage implementation.
 * Throws if called before `setSyncStorage()`.
 */
export function getSyncStorage(): SyncStorage {
  if (!_storage) {
    throw new Error(
      '[SyncStorage] No storage implementation registered. ' +
        'Call setSyncStorage() before using the sync engine. ' +
        "On web, import '../dexie-sync-storage' in your entry point.",
    );
  }
  return _storage;
}

/**
 * Non-throwing probe: `true` once a platform has called
 * {@link setSyncStorage}. Emitters that fire from surfaces where the sync
 * engine may not be initialised — e.g. the personal study-time flush in the
 * logged-out shared-quiz view — gate on this to avoid the "No storage
 * implementation registered" throw. Prefer this over a try/catch so a real
 * storage-write failure still surfaces.
 */
export function isSyncStorageRegistered(): boolean {
  return _storage !== null;
}
